import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, disableTwoFactor, enrollTwoFactor, verifyTwoFactorEnroll } from '@/lib/api-client';
import { useAuth } from '@/app/auth-context';

type WizardStep = 'idle' | 'qr' | 'verify' | 'codes';

export function SettingsSecurityTwoFactorPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();

  const [step, setStep] = useState<WizardStep>('idle');
  const [provisioningUri, setProvisioningUri] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [showSecret, setShowSecret] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [ackSaved, setAckSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Naively assume "currently enabled" when the user lands with confirmed
   * server state. The current /users/me doesn't surface it, so we wait for
   * an "Enable" click to fire enroll and infer enabled state from the
   * success of disable. The s4 admin work can plumb a `two_factor_enabled`
   * boolean into /users/me — out of scope for this sub-step. */

  const beginEnroll = useCallback(async () => {
    if (!user) return;
    setError(null);
    setBusy(true);
    try {
      const r = await enrollTwoFactor(accessToken);
      setProvisioningUri(r.provisioning_uri);
      setRecoveryCodes(r.recovery_codes);
      // Lazy-load the QR encoder — it's ~30 KB gzipped and only needed
      // by users who actively enable 2FA. Closes CodeFix-V1 #39.
      const { default: QRCode } = await import('qrcode');
      const qr = await QRCode.toDataURL(r.provisioning_uri, { width: 260, margin: 1 });
      setQrDataUrl(qr);
      setStep('qr');
    } catch (cause) {
      setError(messageFor(cause, t));
    } finally {
      setBusy(false);
    }
  }, [accessToken, t]);

  const confirmCode = useCallback(async () => {
    if (!user) return;
    setError(null);
    setBusy(true);
    try {
      await verifyTwoFactorEnroll(accessToken, verifyCode.trim());
      setStep('codes');
    } catch (cause) {
      setError(messageFor(cause, t));
    } finally {
      setBusy(false);
    }
  }, [accessToken, t, verifyCode]);

  const disable = useCallback(
    async (code: string) => {
      if (!user) return;
      setError(null);
      setBusy(true);
      try {
        await disableTwoFactor(accessToken, code.trim());
        setStep('idle');
        setProvisioningUri(null);
        setQrDataUrl(null);
        setRecoveryCodes([]);
        setVerifyCode('');
      } catch (cause) {
        setError(messageFor(cause, t));
      } finally {
        setBusy(false);
      }
    },
    [accessToken, t],
  );

  const downloadCodes = useCallback(() => {
    const text = `H3eroMap recovery codes — generated ${new Date().toISOString()}\n\n${recoveryCodes.join('\n')}\n`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'h3eromap-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [recoveryCodes]);

  const copyCodes = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {
      /* clipboard denied — fail silently; the .txt download path still works */
    }
  }, [recoveryCodes]);

  /* Decode the secret out of the provisioning URI for the "show secret"
   * fallback. Uses URLSearchParams on the query portion (the URL
   * constructor can't parse the non-standard otpauth: scheme cleanly).
   * Closes CodeFix-V1 #61. */
  const secret = (() => {
    if (!provisioningUri) return '';
    const queryIdx = provisioningUri.indexOf('?');
    if (queryIdx < 0) return '';
    const params = new URLSearchParams(provisioningUri.slice(queryIdx + 1));
    return params.get('secret') ?? '';
  })();

  return (
    <section className="max-w-2xl mx-auto px-s5 py-s8">
      <header className="mb-s6">
        <h1 className="font-display text-display-lg text-ink">{t('settings.security.twoFactor.title')}</h1>
        <p className="mt-s3 text-gray-2">{t('settings.security.twoFactor.subtitle')}</p>
      </header>

      {step === 'idle' && (
        <IdleSection
          onEnable={() => {
            void beginEnroll();
          }}
          onDisable={disable}
          busy={busy}
          error={error}
        />
      )}

      {step === 'qr' && qrDataUrl && (
        <article data-testid="2fa-step-qr">
          <h2 className="font-display text-display-sm text-ink mb-s3">
            {t('settings.security.twoFactor.wizard.step1Title')}
          </h2>
          <p className="mb-s4 text-ink">{t('settings.security.twoFactor.wizard.step1Instructions')}</p>
          <img src={qrDataUrl} alt="2FA QR code" className="my-s4 border border-bone-3" />
          <div className="mb-s4">
            <button
              type="button"
              className="text-flame-ember hover:underline"
              onClick={() => {
                setShowSecret((v) => !v);
              }}
            >
              {t('settings.security.twoFactor.wizard.showSecret')}
            </button>
            {showSecret && (
              <pre
                className="mt-s2 bg-bone-2 p-s3 font-mono text-sm border border-bone-3"
                data-testid="2fa-secret"
              >
                {secret}
              </pre>
            )}
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setStep('verify');
            }}
            data-testid="2fa-step1-next"
          >
            {t('settings.security.twoFactor.wizard.step1Next')}
          </button>
        </article>
      )}

      {step === 'verify' && (
        <article data-testid="2fa-step-verify">
          <h2 className="font-display text-display-sm text-ink mb-s3">
            {t('settings.security.twoFactor.wizard.step2Title')}
          </h2>
          <p className="mb-s4 text-ink">{t('settings.security.twoFactor.wizard.step2Instructions')}</p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (busy || verifyCode.trim().length < 6) return;
              void confirmCode();
            }}
            className="space-y-s4"
          >
            <div>
              <label htmlFor="verify-code" className="form-label">
                {t('settings.security.twoFactor.wizard.codeLabel')}
              </label>
              <input
                id="verify-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder={t('settings.security.twoFactor.wizard.codePlaceholder')}
                className="input-field"
                value={verifyCode}
                onChange={(e) => {
                  setVerifyCode(e.target.value);
                }}
                data-testid="2fa-verify-input"
              />
            </div>
            {error && (
              <p role="alert" className="form-error">
                {error}
              </p>
            )}
            <div className="flex gap-s3">
              <button type="submit" className="btn-primary" disabled={busy || verifyCode.trim().length < 6} data-testid="2fa-verify-submit">
                {busy
                  ? t('settings.security.twoFactor.wizard.verifyEnrolling')
                  : t('settings.security.twoFactor.wizard.verifyEnroll')}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setStep('qr');
                }}
                disabled={busy}
              >
                {t('settings.security.twoFactor.wizard.back')}
              </button>
            </div>
          </form>
        </article>
      )}

      {step === 'codes' && (
        <article data-testid="2fa-step-codes">
          <h2 className="font-display text-display-sm text-ink mb-s3">
            {t('settings.security.twoFactor.wizard.step3Title')}
          </h2>
          <p className="mb-s4 text-ink">{t('settings.security.twoFactor.wizard.step3Instructions')}</p>
          <p className="mb-s4 text-flame-ember font-medium" role="status">
            ⚠︎ {t('settings.security.twoFactor.wizard.warning')}
          </p>
          <ol className="grid grid-cols-2 gap-s2 bg-bone-2 p-s3 mb-s4 border border-bone-3 font-mono text-sm" data-testid="2fa-recovery-codes">
            {recoveryCodes.map((c) => (
              <li key={c} className="px-s2 py-s1">
                {c}
              </li>
            ))}
          </ol>
          <div className="flex gap-s3 mb-s4">
            <button type="button" className="btn-secondary" onClick={downloadCodes}>
              {t('settings.security.twoFactor.wizard.download')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => void copyCodes()}>
              {copied
                ? t('settings.security.twoFactor.wizard.copied')
                : t('settings.security.twoFactor.wizard.copy')}
            </button>
          </div>
          <label className="flex items-center gap-s2 mb-s4 text-ink">
            <input
              type="checkbox"
              checked={ackSaved}
              onChange={(e) => {
                setAckSaved(e.target.checked);
              }}
              data-testid="2fa-ack-checkbox"
            />
            <span>{t('settings.security.twoFactor.wizard.ack')}</span>
          </label>
          <button
            type="button"
            className="btn-primary"
            disabled={!ackSaved}
            onClick={() => {
              // Enrollment is already confirmed server-side; clear local state.
              setStep('idle');
              setRecoveryCodes([]);
              setProvisioningUri(null);
              setQrDataUrl(null);
              setVerifyCode('');
              setAckSaved(false);
            }}
            data-testid="2fa-finish"
          >
            {t('settings.security.twoFactor.wizard.finish')}
          </button>
        </article>
      )}
    </section>
  );
}

interface IdleSectionProps {
  onEnable: () => void;
  onDisable: (code: string) => Promise<void>;
  busy: boolean;
  error: string | null;
}

function IdleSection({ onEnable, onDisable, busy, error }: IdleSectionProps): JSX.Element {
  const { t } = useTranslation();
  const [disableCode, setDisableCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  // useEffect to clear local state if the parent collapses; not strictly required
  // here but keeps state cleanly bound to mount.
  useEffect(() => () => {
    setDisableCode('');
  }, []);

  return (
    <article>
      <button type="button" className="btn-primary" onClick={onEnable} disabled={busy} data-testid="2fa-enable">
        {t('settings.security.twoFactor.enable')}
      </button>
      {error && (
        <p role="alert" className="form-error mt-s3">
          {error}
        </p>
      )}
      <hr className="my-s6 border-bone-3" />
      <button
        type="button"
        className="text-flame-ember hover:underline"
        onClick={() => {
          setShowDisable((v) => !v);
        }}
        data-testid="2fa-disable-toggle"
      >
        {t('settings.security.twoFactor.disableForm.title')}
      </button>
      {showDisable && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (busy || disableCode.trim().length < 6) return;
            void onDisable(disableCode);
          }}
          className="space-y-s3 mt-s4"
        >
          <p className="text-ink">{t('settings.security.twoFactor.disableForm.instructions')}</p>
          <div>
            <label htmlFor="disable-code" className="form-label">
              {t('settings.security.twoFactor.disableForm.codeLabel')}
            </label>
            <input
              id="disable-code"
              type="text"
              autoComplete="one-time-code"
              placeholder={t('settings.security.twoFactor.disableForm.codePlaceholder')}
              className="input-field"
              value={disableCode}
              onChange={(e) => {
                setDisableCode(e.target.value);
              }}
              data-testid="2fa-disable-input"
            />
          </div>
          <button type="submit" className="btn-primary" disabled={busy || disableCode.trim().length < 6} data-testid="2fa-disable-submit">
            {busy
              ? t('settings.security.twoFactor.disableForm.submitting')
              : t('settings.security.twoFactor.disableForm.submit')}
          </button>
        </form>
      )}
    </article>
  );
}

function messageFor(cause: unknown, t: (k: string) => string): string {
  if (cause instanceof ApiError) {
    if (cause.status === 409) {
      return t('settings.security.twoFactor.errors.alreadyEnabled');
    }
    if (cause.status === 400) {
      return t('settings.security.twoFactor.errors.codeIncorrect');
    }
    if (cause.status === 0) {
      return t('settings.security.twoFactor.errors.network');
    }
    return cause.envelope.message;
  }
  return t('settings.security.twoFactor.errors.network');
}
